import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AdminTable } from '../components/table/AdminTable';
import { AdminToolbar } from '../components/toolbar/AdminToolbar';
import { AdminToolbarTitle } from '../components/toolbar/AdminToolbarTitle';
import { AdminToolbarActions } from '../components/toolbar/AdminToolbarActions';
import { AdminToolbarButton } from '../components/toolbar/AdminToolbarButton';
import { AdminTableActionsItem } from '../components/table/interfaces/AdminTableActionsItem';
import { api } from '../functions/api';
import { ApiPath } from '../api/ApiPath';
import { AliasConflict, EntityAlias } from './dto/EntityAlias';
import { useEntityAliasColumns } from './hooks/useEntityAliasColumns';
import { EntityAliasCreate } from './EntityAliasCreate';
import { AliasConflicts } from './AliasConflicts';
import { AliasPreview } from './AliasPreview';

type Tab = 'conflicts' | 'all';

/**
 * The names the bot answers to.
 *
 * Which of two yards in the same town a word means is not in the data — it is in the heads
 * of the people using the radio. This screen is where that goes, so the vocabulary can be
 * corrected by whoever actually knows it rather than guessed at in code.
 */
export const EntityAliases: FC = () => {
  const { t } = useTranslation();
  const columns = useEntityAliasColumns();

  const [tab, setTab] = useState<Tab>('conflicts');
  const [aliases, setAliases] = useState<EntityAlias[]>([]);
  const [conflicts, setConflicts] = useState<AliasConflict[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [rows, clashes] = await Promise.all([
        api<EntityAlias[]>({ url: ApiPath.EntityAliases, params: search.trim() ? { surface: search.trim() } : {} }),
        api<AliasConflict[]>({ url: ApiPath.EntityAliasConflicts }),
      ]);
      setAliases(rows);
      setConflicts(clashes);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const setRetired = useCallback(
    async (alias: EntityAlias, retired: boolean) => {
      try {
        await api({
          url: ApiPath.EntityAlias.replace(':id', String(alias.id)),
          method: 'patch',
          data: { retired },
        });
        // Retired rather than deleted: the row is how the bot can still say what a word
        // used to mean, and bringing it back is one click rather than retyping it.
        toast.success(retired ? t('Name retired') : t('Name restored'));
        load();
      } catch {
        toast.error(t('Could not change that name'));
      }
    },
    [load, t],
  );

  const actions = useMemo<AdminTableActionsItem<EntityAlias>[]>(
    () => [
      {
        title: t('Retire'),
        onClick: (item) => setRetired(item, true),
      },
      {
        title: t('Restore'),
        onClick: (item) => setRetired(item, false),
      },
    ],
    [setRetired, t],
  );

  return (
    <>
      <AdminToolbar>
        <AdminToolbarTitle>{t('Names the bot knows')}</AdminToolbarTitle>

        <AdminToolbarActions>
          <AdminToolbarButton onClick={() => setAdding('')}>{t('Add a name')}</AdminToolbarButton>
        </AdminToolbarActions>
      </AdminToolbar>

      <AliasPreview />

      {failed && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          {t('Could not load the names')}
          <Button variant="danger" size="sm" onClick={load}>
            {t('Retry')}
          </Button>
        </Alert>
      )}

      <Nav variant="tabs" activeKey={tab} onSelect={(key) => setTab((key as Tab) ?? 'conflicts')} className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="conflicts">
            {t('Needs attention')} {conflicts.length > 0 && `(${conflicts.length})`}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="all">{t('All names')}</Nav.Link>
        </Nav.Item>
      </Nav>

      {tab === 'conflicts' ? (
        <AliasConflicts conflicts={conflicts} loading={loading} onFix={(surface) => setAdding(surface)} />
      ) : (
        <>
          <Form
            className="mb-3"
            onSubmit={(event) => {
              event.preventDefault();
              load();
            }}
          >
            <Form.Control
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('Search for a word').toString()}
            />
          </Form>

          <AdminTable items={aliases} loading={loading} columns={columns} actions={actions} />
        </>
      )}

      <Modal show={adding !== null} onHide={() => setAdding(null)}>
        {adding !== null && (
          <EntityAliasCreate initialSurface={adding} onClose={() => setAdding(null)} onCreated={load} />
        )}
      </Modal>
    </>
  );
};
